import { z } from "zod";
import { Id, IsoDateTime } from "./common";

// ── FieldScopeAssignment ─────────────────────────────────────────────────────

/**
 * Scope types valid for field user assignments. Extends beyond the standard
 * RoleAssignment scopeType to cover mobile-specific scope boundaries.
 */
export const FieldScopeType = z.enum([
  "Site",
  "Building",
  "Floor",
  "Asset",
  "WorkQueue",
  "ServiceProviderOrg",
]);
export type FieldScopeType = z.infer<typeof FieldScopeType>;

export const FieldScopeAssignmentStatus = z.enum(["Active", "Disabled"]);
export type FieldScopeAssignmentStatus = z.infer<typeof FieldScopeAssignmentStatus>;

/**
 * Assigns a field user (FieldWorker or FieldManager) to a specific scope so
 * the backend can enforce which assets, sites, work queues, and history they
 * can access. This is separate from RoleAssignment — role grants permission
 * type, scope assignment grants visibility boundaries.
 */
export const FieldScopeAssignment = z.object({
  fieldScopeAssignmentId: Id,
  tenantId: Id,
  userId: Id,
  role: z.enum(["FieldWorker", "FieldManager"]),
  scopeType: FieldScopeType,
  scopeId: Id,
  /** Populated when the user is ThirdPartyServiceProvider and the scope is tied to an engagement. */
  serviceProviderOrgId: Id.nullable(),
  status: FieldScopeAssignmentStatus,
  createdAt: IsoDateTime,
  createdBy: Id.nullable(),
  disabledAt: IsoDateTime.nullable(),
  disabledBy: Id.nullable(),
});
export type FieldScopeAssignment = z.infer<typeof FieldScopeAssignment>;

export const FieldScopeAssignmentCreateInput = z.object({
  userId: Id,
  role: z.enum(["FieldWorker", "FieldManager"]),
  scopeType: FieldScopeType,
  scopeId: Id,
  serviceProviderOrgId: Id.nullable().optional(),
});
export type FieldScopeAssignmentCreateInput = z.infer<typeof FieldScopeAssignmentCreateInput>;

// ── ServiceProviderOrg ───────────────────────────────────────────────────────

export const ServiceProviderOrgStatus = z.enum(["Active", "Disabled"]);
export type ServiceProviderOrgStatus = z.infer<typeof ServiceProviderOrgStatus>;

/**
 * Represents a third-party service organization that performs work on behalf
 * of an asset-owner tenant. Org membership is recorded on the UserProfile via
 * affiliationType + serviceProviderOrgId.
 */
export const ServiceProviderOrg = z.object({
  serviceProviderOrgId: Id,
  tenantId: Id,
  name: z.string().min(1).max(200),
  status: ServiceProviderOrgStatus,
  primaryContactName: z.string().max(200).nullable(),
  primaryContactEmail: z.string().email().nullable(),
  phone: z.string().max(30).nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
});
export type ServiceProviderOrg = z.infer<typeof ServiceProviderOrg>;

export const ServiceProviderOrgCreateInput = z.object({
  name: z.string().min(1).max(200),
  primaryContactName: z.string().max(200).nullable().optional(),
  primaryContactEmail: z.string().email().nullable().optional(),
  phone: z.string().max(30).nullable().optional(),
});
export type ServiceProviderOrgCreateInput = z.infer<typeof ServiceProviderOrgCreateInput>;

export const ServiceProviderOrgUpdateInput = z
  .object({
    name: z.string().min(1).max(200),
    status: ServiceProviderOrgStatus,
    primaryContactName: z.string().max(200).nullable(),
    primaryContactEmail: z.string().email().nullable(),
    phone: z.string().max(30).nullable(),
  })
  .partial();
export type ServiceProviderOrgUpdateInput = z.infer<typeof ServiceProviderOrgUpdateInput>;

// ── ServiceProviderEngagement ────────────────────────────────────────────────

export const ServiceProviderEngagementStatus = z.enum(["Active", "Expired", "Disabled"]);
export type ServiceProviderEngagementStatus = z.infer<typeof ServiceProviderEngagementStatus>;

/**
 * Defines the relationship between a tenant and a ServiceProviderOrg for a
 * specific period. The allowed* fields constrain what assets, sites, device
 * types, work queues, and commands the service-provider users can access.
 */
export const ServiceProviderEngagement = z.object({
  serviceProviderEngagementId: Id,
  tenantId: Id,
  serviceProviderOrgId: Id,
  status: ServiceProviderEngagementStatus,
  allowedSiteIds: z.array(Id),
  allowedAssetIds: z.array(Id),
  allowedDeviceTypes: z.array(z.string().min(1)),
  allowedWorkQueues: z.array(z.string().min(1)),
  allowedCommandTypes: z.array(z.string().min(1)),
  startDate: IsoDateTime,
  endDate: IsoDateTime.nullable(),
  supportAgreementType: z.string().max(100).nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
});
export type ServiceProviderEngagement = z.infer<typeof ServiceProviderEngagement>;

export const ServiceProviderEngagementCreateInput = z.object({
  serviceProviderOrgId: Id,
  allowedSiteIds: z.array(Id).default([]),
  allowedAssetIds: z.array(Id).default([]),
  allowedDeviceTypes: z.array(z.string().min(1)).default([]),
  allowedWorkQueues: z.array(z.string().min(1)).default([]),
  allowedCommandTypes: z.array(z.string().min(1)).default([]),
  startDate: IsoDateTime,
  endDate: IsoDateTime.nullable().optional(),
  supportAgreementType: z.string().max(100).nullable().optional(),
});
export type ServiceProviderEngagementCreateInput = z.infer<
  typeof ServiceProviderEngagementCreateInput
>;

// ── Mobile / Portal /me response schemas ────────────────────────────────────

export const FieldScopeAssignmentSummary = z.object({
  fieldScopeAssignmentId: Id,
  role: z.enum(["FieldWorker", "FieldManager"]),
  scopeType: FieldScopeType,
  scopeId: Id,
});
export type FieldScopeAssignmentSummary = z.infer<typeof FieldScopeAssignmentSummary>;

/** Response shape for GET /mobile/me */
export const MobileMe = z.object({
  userId: Id,
  tenantId: Id,
  email: z.string().email(),
  displayName: z.string(),
  fieldRole: z.enum(["FieldWorker", "FieldManager"]),
  affiliationType: z.enum(["Employee", "Contractor", "ThirdPartyServiceProvider"]),
  serviceProviderOrg: ServiceProviderOrg.nullable(),
  permissions: z.array(z.string()),
  scopeAssignments: z.array(FieldScopeAssignmentSummary),
  featureFlags: z.array(z.string()),
});
export type MobileMe = z.infer<typeof MobileMe>;

/** Response shape for GET /portal/me */
export const PortalMe = z.object({
  userId: Id,
  tenantId: Id,
  email: z.string().email(),
  displayName: z.string(),
  portalRole: z.enum(["PortalViewer", "PortalOperator"]),
  permissions: z.array(z.string()),
  scopeAssignments: z.array(FieldScopeAssignmentSummary),
  featureFlags: z.array(z.string()),
});
export type PortalMe = z.infer<typeof PortalMe>;
